import { createFileRoute } from '@tanstack/react-router';
import { PageShell } from '~/components/layout';
import {
  CtaBanner,
  DownloadQr,
  FaqAccordion,
  FeatureBlock,
  Hero,
  QuoteTestimonials,
  TextSection,
} from '~/components/sections';
import { getTestimonials } from '~/lib/payload';

/** Loader payload shared by the English and Arabic business routes. */
export type BusinessQuotes = Awaited<ReturnType<typeof getTestimonials>>;
import { DEFAULT_LANGUAGE } from '~/lib/language';
import { pageHeadMeta } from '~/lib/seo';
import { useContent, useLanguage } from '~/lib/useLanguage';
import heroCss from '~/styles/hero.css?inline';

export const Route = createFileRoute('/business')({
  loader: () => getTestimonials({ data: 'business' }),
  head: () =>
    pageHeadMeta({
      title: 'International USD, EUR, GBP Business Accounts',
      description:
        'For businesses, agencies, and registered entities (LLC, Ltd, etc.). Multi-currency accounts to receive from international clients (USD, EUR, GBP). Easy payroll for you and your team.',
      path: '/business',
      inlineStyles: [heroCss],
    }),
  component: BusinessPage,
});

function BusinessPage() {
  return <BusinessPageContent quotes={Route.useLoaderData()} />;
}

/**
 * Page body, parameterized by its loader data.
 *
 * Deliberately does NOT call `Route.useLoaderData()` itself: the Arabic route
 * (`/ar/business`) renders this same component, and reading the `/business` route's
 * loader from a different active match throws
 * "Could not find an active match from /business". Each route reads its own loader
 * and passes the result in.
 */
export function BusinessPageContent({ quotes }: { quotes: BusinessQuotes }) {
  const language = useLanguage();
  const { business, shared, ui } = useContent();
  const { businessHero, businessReceive, businessInvoices, businessPayTeam } = business;

  return (
    <PageShell>
      <Hero content={businessHero} />
      <TextSection content={shared.trustedBySection} />
      <FeatureBlock content={businessReceive} imagePosition="left" />
      <FeatureBlock content={businessPayTeam} imagePosition="right" ctaSlot={<DownloadQr />} />
      <FeatureBlock content={businessInvoices} imagePosition="left" />
      <TextSection content={shared.safeSpaceSection} sectionClassName="max-md:!my-[40px]" />
      <QuoteTestimonials title={ui.business.testimonialsTitle} quotes={quotes} />
      {/* Payload holds the FAQ in English only, so translated pages read theirs
          from the content bundle. */}
      <FaqAccordion content={language === DEFAULT_LANGUAGE ? undefined : shared.defaultFaq} />
      <CtaBanner content={shared.defaultCtaBanner} />
    </PageShell>
  );
}
